<template>
  <div id="Stats">
    <Toolbar :title="t('Reports')">
      <div slot="buttons">
        <v-btn icon @click="copy" :disabled="!executed">
          <v-icon>file_copy</v-icon>
        </v-btn>
      </div>
    </Toolbar>

    <v-expansion-panel v-model="active">
      <v-expansion-panel-content>
        <div slot="header" class="ml-4">{{ t('Parameters') }}</div>
        <v-card>
          <v-card-text id="cell-type" v-if="types.length > 1" class="py-0">
            <div>{{ t('Cell type') }}:</div>
            <v-radio-group v-model="cellTypeName" row class="mt-0">
              <v-radio v-for="p in types" :key="p" :label="t(p)" :value="p" />
            </v-radio-group>

            <!-- <v-checkbox v-for="(name, i) in types" :key="i" :label="t(name)"
              :inputValue="checkedTypes[i]" @change="toggleType(i)"/> -->
          </v-card-text>

          <v-card-text class="py-0 ml-2">
            <v-radio-group v-model="period" row class="mt-0" style="justify-content: flex-start">
              <v-radio v-for="p in periods" :key="p" :label="t(p)" :value="p" />
            </v-radio-group>

            <v-radio-group v-model="startingPoint" row class="mt-0" style="justify-content: flex-start">
              <v-radio v-for="p in startingPoints" :key="p" :label="t(p)" :value="p" />
            </v-radio-group>
          </v-card-text>
          <v-spacer />

          <v-card-actions class="pl-4">
            <v-btn @click="run">{{ t('Perform') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-expansion-panel-content>

      <v-expansion-panel-content v-show="executed">
        <div slot="header" class="ml-4">{{ t('Report') }}</div>
        <v-tabs v-model="activeTab">
          <v-tab>{{ t('Persons') }}</v-tab>
          <v-tab>{{ t('Cells') }}</v-tab>
          <v-tab v-if="showSections">{{ t('Sections') }}</v-tab>

          <!-- Persons -->
          <v-tab-item>
            <v-card>
              <v-card-text>
                <v-data-table id="data" :headers="personHeaders" :items="personStats" hide-actions class="elevation-1">
                  <template slot="items" slot-scope="props">
                    <tr :class="{ 'last-in-cell': isLastInCell(props) }">
                      <td v-for="h in personHeaders" :key="h.value" v-html="props.item[h.value]"></td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Cells -->
          <v-tab-item>
            <v-card>
              <v-card-text>
                <v-data-table id="data" :headers="cellHeaders" :items="cellStats" hide-actions class="elevation-1">
                  <template slot="items" slot-scope="props">
                    <td v-for="h in cellHeaders" :key="h.value" v-html="props.item[h.value]"></td>
                  </template>
                  <template slot="footer">
                    <tr>
                      <td v-for="(s, i) in summary" :key="i" v-html="s"></td>
                    </tr>
                  </template>
                </v-data-table>

                <div class="mb-2 mt-2">
                  {{ t('Legend') }}:
                  <table cellspan="6">
                    <tr>
                      <td>{{ t('Number of persons') }}</td>
                      <td>- {{ t('Number of persons visiting during the period') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Average frequency') }}</td>
                      <td>- {{ t('Average frequency of members on meetings') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Average person count') }}</td>
                      <td>- {{ t('Average person count on a meeting') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Meeting span') }}</td>
                      <td>- {{ t('Meetings frequency in weeks') }}</td>
                    </tr>
                  </table>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Sections -->
          <v-tab-item>
            <v-card>
              <v-card-text>
                <v-data-table
                  id="data"
                  :headers="sectionHeaders"
                  :items="sectionStats"
                  hide-actions
                  class="elevation-1"
                >
                  <template slot="items" slot-scope="props">
                    <td v-for="h in sectionHeaders" :key="h.value" v-html="props.item[h.value]"></td>
                  </template>
                </v-data-table>

                <div class="mb-2 mt-2">
                  {{ t('Legend') }}:
                  <table cellspan="6">
                    <tr>
                      <td>{{ t('Number of persons') }}</td>
                      <td>- {{ t('Number of persons visiting during the period') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Average frequency') }}</td>
                      <td>- {{ t('Average frequency of members on meetings') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Average person count') }}</td>
                      <td>- {{ t('Average person count on a meeting') }}</td>
                    </tr>
                    <tr>
                      <td>{{ t('Meeting span') }}</td>
                      <td>- {{ t('Meetings frequency in weeks') }}</td>
                    </tr>
                  </table>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </div>
</template>

<script>
import Toolbar from '@/components/layout/Toolbar'
import { mapState, mapGetters, mapActions } from 'vuex'
import { mapStateMutations } from '@/store'
import { compareRoleRank } from '@/global'
// import { t } from '@/i18n'

export default {
  components: { Toolbar },

  data() {
    return {
      cellTypeName: 'Open cell',
      periods: ['Month', 'Quarter', 'Half year', 'Year'],
      period: 'Quarter',
      startingPoints: ['Until now', 'Current', 'Previous'],
      startingPoint: 'Until now',
      executed: false,
      active: 0,
    }
  },

  async created() {
    await this.$store.dispatch('authorize')
  },

  // mounted() {
  //   this.$nextTick(() => {
  //     const r = document.querySelector('.v-expansion-panel__header').getBoundingClientRect()
  //     console.log(r)
  //     document.querySelector('.v-expansion-panel__body').styles.height = `calc(100vh - ${r.bottom}px)`
  //   })
  // },

  computed: {
    ...mapState('cell', ['item', 'type', 'names']),
    ...mapState('stats2', ['personStats', 'cellStats', 'sectionStats', 'summary', 'total']),
    ...mapGetters('stats2', ['personHeaders', 'cellHeaders', 'sectionHeaders', 'types', 'role']),
    activeTab: mapStateMutations('stats2/activeTab'),
    from: mapStateMutations('stats2/from'),
    to: mapStateMutations('stats2/to'),
    showSections() {
      console.log(this.$store.getters['stats2/role'], compareRoleRank(this.$store.getters['stats2/role'], 'L'))
      return compareRoleRank(this.$store.getters['stats2/role'], 'L') > 0
    },
  },

  methods: {
    ...mapActions('stats2', ['copy', 'find']),

    isLastInCell(props) {
      const a = this.personStats
      const i = props.index
      // console.log(i, a[i + 1].cell, props.item.cell)
      return i < a.length - 1 && a[i + 1].cell !== props.item.cell
    },
    run() {
      this.find(this)
      this.$store.commit('maxWidth', '100%')
      this.$nextTick(() => {
        this.executed = true
        this.active = 1
        this.$nextTick(() => this.$store.commit('stats2/activeTab', 0))
      })
    },
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('maxWidth')
    next()
  },
}
</script>

<style lang="stylus">
#cell-type {
  display: flex;
}

.v-input__slot {
  margin-bottom: 0px !important;
}

.last-in-cell {
  border-bottom: 1px solid #808080 !important;
}

.v-table__overflow {
  width: auto !important;
}

table.v-datatable.v-table {
  width: auto !important;
}

#cell-type > div {
  vertical-align: middle;
  margin: 0px;
  padding: 0px 8px;
  flex: none;
}

#data {
  border-top: 1px solid rgba(128, 128, 128, 0.25);

  tr {
    height: 32px;
  }

  th, td {
    padding: 0px 4px;
    height: 32px;
    max-height: 32px;
    min-width: 70px;
    white-space: nowrap;
    text-align: center;
    border-right: 1px solid grey;
    border-right-color: rgba(128, 128, 128, 0.12);
  }

  td:first-child {
    text-align: left;
  }

  tfoot {
    background-color, tr:first-child {
      border-top: 1px solid grey;
    }

    td {
      color: rgba(0, 0, 0, 0.54);
      font-size: 12px;
      font-weight: 500;
    }
  }
}
</style>
